/**
 * useRealtimeSubscription — internal helper that wires a single
 * Supabase Realtime channel to a React Query invalidation set.
 *
 * Used by the public hooks below:
 *   - useRealtimeNotifications(userId)
 *   - useRealtimeTaskAssignments(userId)
 *
 * The hook subscribes on mount (when `enabled !== false`), disposes
 * the channel on unmount, and re-subscribes when any of the inputs
 * that affect channel identity change.
 */

import { useEffect } from 'react'
import { useQueryClient }                        from '@tanstack/react-query'
import type { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js'
import { useSupabase }                           from './useSupabase'

export type PostgresEvent = 'INSERT' | 'UPDATE' | 'DELETE'

export interface RealtimeOptions {
  /** Stable identifier for this channel — must be unique per (table, filter) pair. */
  channelName: string
  /** Postgres table to subscribe to (in the public schema). */
  table: string
  /**
   * Postgres `filter` expression, e.g. `user_id=eq.<uuid>`.
   * If empty / undefined, no row-level filter is applied.
   */
  filter?: string
  /** Which DML events to listen for. Default ['INSERT']. */
  events?: readonly PostgresEvent[]
  /** Query keys to invalidate when an event arrives. */
  invalidateKeys: ReadonlyArray<readonly unknown[]>
  /** When false, the hook is a no-op — useful before the user JWT is known. */
  enabled?: boolean
}

export function useRealtimeSubscription(opts: RealtimeOptions): void {
  const supabase = useSupabase()
  const qc       = useQueryClient()

  // Stable serialized identity for the array inputs — the effect re-runs
  // when the *content* changes, regardless of array reference identity.
  const enabled            = opts.enabled !== false
  const eventsJson         = JSON.stringify(opts.events ?? ['INSERT'])
  const invalidateKeysJson = JSON.stringify(opts.invalidateKeys)

  useEffect(() => {
    if (!enabled) return

    const events = JSON.parse(eventsJson) as PostgresEvent[]
    const keys   = JSON.parse(invalidateKeysJson) as unknown[][]

    const channel: RealtimeChannel = supabase.channel(opts.channelName)

    const onChange = (_payload: RealtimePostgresChangesPayload<Record<string, unknown>>): void => {
      for (const key of keys) {
        void qc.invalidateQueries({ queryKey: key })
      }
    }

    for (const event of events) {
      channel.on(
        // The supabase-js v2 typings widen this string union narrowly per overload;
        // the cast keeps the call type-safe at the runtime boundary.
        'postgres_changes' as 'system',
        {
          event,
          schema: 'public',
          table:  opts.table,
          ...(opts.filter ? { filter: opts.filter } : {}),
        } as never,
        onChange as never,
      )
    }

    channel.subscribe()

    return (): void => {
      void supabase.removeChannel(channel)
    }
  }, [supabase, qc, opts.channelName, opts.table, opts.filter, eventsJson, invalidateKeysJson, enabled])
}
